import constants from 'config/constants'
import { QuestionType } from 'packages/question-types'

export const initialNPSPrimaryQuestion = {
  metric: constants.questionMetricType.nps,
  type: constants.questionType.slider,
  text: 'How likely are you to recommend us to a friend or colleague?',
  leftSideLabel: 'Not at all likely',
  rightSideLabel: 'Extremely likely',
  displayFollowUpQuestion: true,
  isOpinionScale: true,
  isRequired: true,
  followUpQuestions: [
    {
      text: "We're sorry to have let you down, what could we do to get closer to a 10?",
      sentiment: 'negative',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
    {
      text: 'What could we do to get closer to a 10?',
      sentiment: 'neutral',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
    {
      text: "Was there anything in particular we've done that has delighted you?",
      sentiment: 'positive',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
  ],
  numberOfChoices: 11,
  conditionalLogicType: 'sentiment',
}

export const initialCSATPrimaryQuestion = {
  metric: constants.questionMetricType.csat,
  type: constants.questionType.multipleChoice,
  text: 'How did we do?',
  choiceIconSet: null,
  choices: ["Bad, I'm unsatisfied", 'Okay, but not great', "Great, I'm satisfied"],
  displayFollowUpQuestion: true,
  isOpinionScale: true,
  isRequired: true,
  followUpQuestions: [
    {
      text: "We're sorry to have let you down, how could we improve?",
      sentiment: 'negative',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
    {
      text: 'Can you share your experience and how we could have done a little better?',
      sentiment: 'neutral',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
    {
      text: 'Was there anything in particular we did that delighted you?',
      sentiment: 'positive',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
  ],
  conditionalLogicType: 'sentiment',
}

export const initialScreenerQuestion = {
  text: 'Was your issue resolved?',
  type: QuestionType.MULTIPLE_CHOICE,
  metric: 'CUSTOM',
  // Sibling constants (initialNPSPrimaryQuestion / initialCSATPrimaryQuestion)
  // all use camelCase because these are form-state templates seeded into the
  // Formik values object — camelize happens at BE-payload egress
  // (`transformResponseToPresentationalState`). `findMetricQuestion`/
  // `isMetricQuestion` accept both shapes so this is a pure convention cleanup.
  isPrimaryMetric: false,
  choices: [
    { text: 'Yes', order: 1, action: 'display_follow_up_question' },
    { text: 'No', order: 2, action: 'finish_survey' },
  ],
}

// Keys accepted by `getQuestionTemplate`. Mirrors `constants.questionMetricType`
// plus the PET-1965 screener template, which is not a metric type — so it lives
// here instead of extending the global metric-type constant.
export const questionTemplateType = {
  ...constants.questionMetricType,
  screener: 'screener',
}

export const getAutoId = function (length = 6) {
  return Math.random().toString(20).substr(2, length)
}
export const TEMP_ID_PREFIX = 'temp_id_'
export const MAX_CHOICES_PER_QUESTION = 10
export const TEMP_ID_PRIMARY_PREFIX = TEMP_ID_PREFIX + 'primary_'

export const questionTypeJsonData = {
  get comment() {
    return {
      uniqueIdentifier: `${TEMP_ID_PREFIX}${getAutoId()}`,
      metric: constants.questionMetricType.custom,
      type: constants.questionType.comment,
      text: '',
      isRequired: false,
    }
  },
  get multipleChoice() {
    return {
      uniqueIdentifier: `${TEMP_ID_PREFIX}${getAutoId()}`,
      metric: constants.questionMetricType.custom,
      type: constants.questionType.multipleChoice,
      text: '',
      isOpinionScale: true,
      isRequired: false,
      choices: [
        {
          id: `${TEMP_ID_PREFIX}${getAutoId()}`,
          label: null,
          value: 4,
          sentiment: 'positive',
        },
        {
          id: `${TEMP_ID_PREFIX}${getAutoId()}`,
          label: null,
          value: 2,
          sentiment: 'negative',
        },
      ],
    }
  },
  get multi_select_choice() {
    return {
      uniqueIdentifier: `${TEMP_ID_PREFIX}${getAutoId()}`,
      metric: constants.questionMetricType.custom,
      type: constants.questionType.multi_select_choice,
      text: '',
      isOpinionScale: false,
      isRequired: false,
      choices: [
        {
          id: `${TEMP_ID_PREFIX}${getAutoId()}`,
          label: null,
          value: null,
          sentiment: null,
        },
        {
          id: `${TEMP_ID_PREFIX}${getAutoId()}`,
          label: null,
          value: null,
          sentiment: null,
        },
      ],
    }
  },
  get slider() {
    return {
      uniqueIdentifier: `${TEMP_ID_PREFIX}${getAutoId()}`,
      metric: constants.questionMetricType.custom,
      type: constants.questionType.slider,
      text: '',
      leftSideLabel: 'Not at all likely',
      rightSideLabel: 'Extremely likely',
      isOpinionScale: true,
      isRequired: false,
      numberOfChoices: 11,
      choices: Array.from({ length: 11 }, (_, i) => ({
        id: `${TEMP_ID_PREFIX}${getAutoId()}`,
        label: i == 0 ? 'Not at all likely' : i == 10 ? 'Exteremely likely' : i.toString(),
        value: i,
        sentiment: i <= 7 ? 'negative' : i <= 9 ? 'neutral' : 'positive',
      })),
    }
  },
  get file_upload() {
    return {
      uniqueIdentifier: `${TEMP_ID_PREFIX}${getAutoId()}`,
      metric: constants.questionMetricType.custom,
      type: constants.questionType.file_upload,
      text: '',
      isRequired: false,
    }
  },
}

export const initialCESPrimaryQuestion = (companyName) => ({
  metric: constants.questionMetricType.ces,
  type: constants.questionType.rating_scale_minmax,
  text: `${companyName} made it easy for me to handle my issue`,
  leftSideLabel: 'Strongly disagree',
  rightSideLabel: 'Strongly agree',
  displayFollowUpQuestion: true,
  isOpinionScale: true,
  isRequired: true,
  followUpQuestions: [
    {
      text: "We're sorry to have let you down. How could we make it easier next time?",
      sentiment: 'negative',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
    {
      text: 'How could we have made it easier?',
      sentiment: 'neutral',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
    {
      text: "Was there anything in particular we've done that has delighted you?",
      sentiment: 'positive',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
  ],
  numberOfChoices: 7,
  conditionalLogicType: 'sentiment',
})

export const initialFiveStarPrimaryQuestion = (companyName) => ({
  metric: constants.questionMetricType.star,
  type: constants.questionType.star,
  text: `How would you rate your experience with ${companyName}?`,
  choiceIconSet: null,
  leftSideLabel: 'Dissatisfied',
  rightSideLabel: 'Outstanding!',
  displayFollowUpQuestion: true,
  isOpinionScale: true,
  isRequired: true,
  followUpQuestions: [
    {
      text: "We're sorry to have let you down, how could we improve?",
      sentiment: 'negative',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
    {
      text: 'Can you share your experience and how we could have done a little better?',
      sentiment: 'neutral',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
    {
      text: 'Was there anything in particular we did that delighted you?',
      sentiment: 'positive',
      type: QuestionType.COMMENT,
      isRequired: false,
    },
  ],
  numberOfChoices: 5,
  conditionalLogicType: 'sentiment',
})



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/SurveySettingsV2/constants.js